import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import * as http from 'utils/http';
import { login } from 'actions/auth';
import { injectIntl } from 'react-intl';

import styled from 'styled-components';
import { Button, Field, Link } from '@ontruck/orc';

import queryString from 'query-string';

import config from 'config';
import { useHistory } from 'react-router-dom';
import { LoginIcon } from 'assets/icons/icon_by_config/LoginIcon';
import Separator from '../../modules/emergencies/components/ui/layout/Separator';

const actionCreators = {
  login,
};

const LoginContainerDiv = styled.div`
  background: var(--login-bg-color);
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledLogo = styled.img`
  margin-bottom: 24px;
  width: 260px;
`;

const StyledField = styled(Field)`
  border-radius: 4px;
  width: 348px;
  margin-bottom: 8px;

  input {
    border-radius: 4px;
    background: #fff;
  }
`;

const ErrorMessage = styled.p`
  font-size: 14px;
  color: var(--error-500);
  margin: 0 0 8px;
`;

const LoginButton = styled(Button)`
  background: var(--login-button-bg);
  color: var(--login-button-color);
  margin-top: 10px;
  font-size: 14px;
  font-weight: bold;

  :hover {
    background: var(--login-button-bg-hover);
    color: var(--login-button-color);
  }
`;

const StyledLink = styled(Link)`
  margin-top: 1rem;
`;

const LoginSSOButton = styled(LoginButton)`
  background: var(--login-button-color);
  color: var(--login-button-bg);
  font-weight: bold;
  width: 348px;
  border: 1px solid var(--login-button-bg);

  :hover {
    background: var(--login-button-bg-hover);
  }
`;

const LoginContainerSSO = styled.div`
  justify-content: center;
`;

const StyledSeparator = styled(Separator)`
  height: 0.4px;
  margin-top: 10px;
`;

const LoginContainer = ({ login, history: { replace }, location: { search }, intl }) => {
  const t = intl.messages.containers.Login;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const history = useHistory();

  const redirectFunction = useCallback(() => {
    const { redirect: redirectUrl } = queryString.parse(search);
    replace(redirectUrl || config.BASE_ROUTE);
  }, [replace, search]);

  const handleLogin = useCallback(
    (access_token, response) => {
      const user = {
        name: 'Test',
        email: username,
        avatar: '',
      };
      login(access_token, user, response, redirectFunction);
    },
    [login, redirectFunction, username]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await http.rawPost('/v1/auth/token/', {
        username,
        password,
        client_id: config.ONTRUCK_AUTH_CLIENTID,
        grant_type: 'password',
      });
      localStorage.removeItem('useSAML');
      handleLogin(response.access_token, response);
    } catch (e) {
      setError(true);
    }
  };

  const handleClick = () => {
    history.push('password_reset');
  };

  const handleSSOOptionClick = () => {
    localStorage.setItem('useSAML', true);
  };

  const isLoginUserPasswordEnabled = config.AUTH_TYPES.includes('user_password');
  const isLoginSSOSamlEnabled = config.AUTH_TYPES.includes('sso_saml');

  return (
    <LoginContainerDiv>
      {isLoginUserPasswordEnabled && (
        <>
          <StyledLogo alt="Logo" src={LoginIcon()} />
          <form onSubmit={handleSubmit}>
            <StyledField
              id="username"
              role="username"
              type="email"
              name="username"
              onChange={(e) => {
                setUsername(e.target.value);
                setError(false);
              }}
              label={t.username_label}
              fullWidth
              value={username}
              error={error}
            />
            <StyledField
              id="password"
              role="password"
              name="password"
              onChange={(e) => {
                setPassword(e.target.value);
                setError(false);
              }}
              label={t.password_label}
              type="password"
              fullWidth
              value={password}
              error={error}
            />
            {error && <ErrorMessage>{t.login_error}</ErrorMessage>}
            <LoginButton
              role="login"
              id="submit"
              type="submit"
              size="L"
              fullWidth
              text={t.login_btn}
            />
          </form>
          <StyledLink skin="secondary" text={t.click} onClick={handleClick} />
        </>
      )}
      {isLoginSSOSamlEnabled && (
        <LoginContainerSSO onClick={handleSSOOptionClick}>
          {isLoginUserPasswordEnabled && <StyledSeparator />}
          <a href={config.SAML_CONFIG.LOGIN.url}>
            <LoginSSOButton role="login" id="sso" size="L" fullWidth text={t.sso_btn} />
          </a>
        </LoginContainerSSO>
      )}
    </LoginContainerDiv>
  );
};

export default injectIntl(connect(null, actionCreators)(LoginContainer));
